import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import CallButton from "../components/CallButton";
import {
  ArrowLeft, ChevronLeft, ChevronRight, Mail, Phone, Globe, Send, TrendingUp, Calendar, Save, Trash2, MessageSquare, DollarSign, AlertTriangle,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageLoader, Select, toast, Avatar, Spinner, Copyable } from "../components/ui";
import { LEAD_STATUSES, CLIENT_STATUSES, statusMeta, countryName, countryFlag, money } from "../lib/constants";
import { format } from "date-fns";

function Row({ icon: Icon, label, value, copyable }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 dark:border-slate-800 px-3 py-2.5">
      <div className="flex items-center gap-3 min-w-0 w-full">
        <Icon className="w-4 h-4 text-slate-400 shrink-0" />
        <div className="min-w-0 w-full">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
          {copyable && value ? (
            <Copyable value={value} testid={`copy-${label.toLowerCase()}`}
              className="w-full text-sm font-medium text-slate-800 dark:text-slate-100 hover:text-indigo-600 dark:hover:text-indigo-400">
              <span className="truncate">{value}</span>
            </Copyable>
          ) : (
            <div className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">{value || "—"}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ContactDetail({ type }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // The filters the list had when this record was opened - prev/next step
  // through that exact filtered list, same scope as GET /clients.
  const listFilters = {
    source: searchParams.get("source") || "",
    status: searchParams.get("status") || "",
    country: searchParams.get("country") || "",
    search: searchParams.get("search") || "",
    assigned: searchParams.get("assigned") || "",
  };
  const source = listFilters.source;
  const activeFilters = Object.fromEntries(Object.entries(listFilters).filter(([, v]) => v));
  const filterQuery = new URLSearchParams(activeFilters).toString();
  const filterSuffix = filterQuery ? `?${filterQuery}` : "";
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [d, setD] = useState(null);
  const [neighbors, setNeighbors] = useState(null);
  const [dups, setDups] = useState(null);
  const [agents, setAgents] = useState([]);
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState({});
  const [comment, setComment] = useState("");
  const [dep, setDep] = useState({ amount: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get(`/clients/${id}`);
    setD(r.data);
    const c = r.data.client;
    setForm({
      name: c.name || "", email: c.email || "", phone: c.phone || "", country: c.country || "",
      client_type: c.client_type, lead_status: c.lead_status, client_status: c.client_status,
      assigned_agent_id: c.assigned_agent_id || "", crypto_address: c.crypto_address || "", callback_at: c.callback_at || "",
      source: c.source || "",
    });
  }, [id]);

  const loadDups = useCallback(() => {
    api.get(`/clients/${id}/duplicates`).then((r) => setDups(r.data)).catch(() => setDups(null));
  }, [id]);

  useEffect(() => { load(); loadDups(); }, [load, loadDups]);
  useEffect(() => { api.get("/clients/sources").then((r) => setSources(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (isAdmin) api.get("/users/simple").then((r) => setAgents(r.data)).catch(() => {}); }, [isAdmin]);
  useEffect(() => {
    api.get(`/clients/${id}/neighbors`, { params: { type, ...activeFilters } })
      .then((r) => setNeighbors(r.data))
      .catch(() => {});
  }, [id, type, filterQuery]);

  const goNeighbor = (n) => n && nav(`/${type}s/${n.id}${filterSuffix}`);

  if (!d) return <PageLoader />;
  const c = d.client;
  const isLead = c.client_type === "lead";
  const statuses = isLead ? LEAD_STATUSES : CLIENT_STATUSES;

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, email: form.email, phone: form.phone, country: form.country,
        client_type: form.client_type, assigned_agent_id: form.assigned_agent_id || null,
        crypto_address: form.crypto_address, source: form.source || undefined,
      };
      payload[isLead ? "lead_status" : "client_status"] = isLead ? form.lead_status : form.client_status;
      await api.put(`/clients/${id}`, payload);
      toast("Saved");
      load();
      loadDups();
    } catch (e) { toast(e.response?.data?.detail || "Failed", "error"); } finally { setSaving(false); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    const r = await api.post(`/clients/${id}/comments`, { content: comment, is_quick_comment: false });
    setD((p) => ({ ...p, comments: [r.data, ...p.comments] }));
    setComment("");
    toast("Comment added");
  };

  const addDeposit = async () => {
    const amt = parseFloat(dep.amount);
    if (!amt || amt <= 0) return toast("Enter a valid amount", "error");
    try {
      const r = await api.post(`/clients/${id}/deposits`, { amount: amt, notes: dep.notes || null });
      setDep({ amount: "", notes: "" });
      toast("Deposit added");
      load();
    } catch (e) { toast(e.response?.data?.detail || "Failed", "error"); }
  };

  const schedule = async () => {
    const val = prompt("Schedule appointment (YYYY-MM-DD HH:MM)", "");
    if (val === null) return;
    try {
      const iso = val ? new Date(val.replace(" ", "T")).toISOString() : null;
      await api.post(`/clients/${id}/schedule`, { callback_at: iso });
      toast("Appointment scheduled");
      load();
    } catch { toast("Invalid date", "error"); }
  };

  const del = async () => {
    if (!window.confirm("Delete this contact permanently?")) return;
    await api.delete(`/clients/${id}`);
    toast("Deleted");
    nav(`/${type}s`);
  };

  const sMeta = statusMeta(c.client_type, isLead ? c.lead_status : c.client_status);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button className="btn-secondary p-2" onClick={() => nav(`/${type}s`)} data-testid="back-btn"><ArrowLeft className="w-4 h-4" /></button>
          {neighbors?.prev && (
            <button className="btn-secondary text-xs sm:text-sm max-w-[170px]" onClick={() => goNeighbor(neighbors.prev)}
              data-testid="prev-contact-btn" title={`Previous${source ? ` in ${source}` : ""}: ${neighbors.prev.name}`}>
              <ChevronLeft className="w-4 h-4 shrink-0" /><span className="ml-0.5 hidden sm:inline truncate">{neighbors.prev.name}</span>
            </button>
          )}
          {neighbors?.next && (
            <button className="btn-secondary text-xs sm:text-sm max-w-[170px]" onClick={() => goNeighbor(neighbors.next)}
              data-testid="next-contact-btn" title={`Next${source ? ` in ${source}` : ""}: ${neighbors.next.name}`}>
              <span className="mr-0.5 hidden sm:inline truncate">{neighbors.next.name}</span><ChevronRight className="w-4 h-4 shrink-0" />
            </button>
          )}
          {neighbors && neighbors.position > 0 && neighbors.total > 0 && (
            <span className="text-xs font-medium text-slate-400 dark:text-slate-500 tabular-nums shrink-0"
              data-testid="position-indicator" title={filterQuery ? "Position in the current filtered list" : "Position in list"}>
              {neighbors.position}/{neighbors.total.toLocaleString()}
            </span>
          )}
          <Avatar name={c.name} size="h-11 w-11" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl sm:text-2xl font-bold font-display text-slate-900 dark:text-white" data-testid="detail-name">{c.name}</h1>
              <span className={`badge ${c.client_type === "client" ? "bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-800" : "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700"}`}>{c.client_type}</span>
              {c.balance > 0 && <span className="badge bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800">{money(c.balance)}</span>}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">Created {c.created_at ? format(new Date(c.created_at), "MMM d, yyyy 'at' h:mm a") : ""}</div>
          </div>
        </div>
        {isAdmin && <button className="btn-secondary text-rose-600 dark:text-rose-400" onClick={del} data-testid="delete-contact-btn"><Trash2 className="w-4 h-4" /> Delete</button>}
      </div>

      {dups && dups.count > 0 && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-500/10 p-4" data-testid="duplicate-banner">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-700 dark:text-amber-400">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            Possible duplicate — {dups.count} other record{dups.count > 1 ? "s" : ""} {dups.count > 1 ? "share" : "shares"} this email or phone.
          </div>
          <div className="mt-2 space-y-1">
            {(dups.duplicates || []).slice(0, 5).map((x) => (
              <Link key={x.id} to={`/${x.client_type === "client" ? "clients" : "leads"}/${x.id}`} className="flex items-center gap-2 text-xs text-amber-800 dark:text-amber-300 hover:underline">
                <span className="font-medium">{x.name}</span>
                <span className="text-amber-700/70 dark:text-amber-400/70">{x.email || x.phone}</span>
                {x.created_at && <span className="text-amber-600/60 dark:text-amber-500/50">{format(new Date(x.created_at), "MMM d, yyyy")}</span>}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: contact + admin */}
        <div className="lg:col-span-4 space-y-6">
          <div className="card p-5">
            <h3 className="font-semibold font-display text-slate-900 dark:text-white mb-4 flex items-center gap-2"><Mail className="w-4 h-4 text-indigo-500" /> Contact Details</h3>
            <div className="space-y-2">
              <Row icon={Mail} label="Email" value={c.email} copyable />
              <Row icon={Phone} label="Phone" value={c.phone} copyable />
              <Row icon={Globe} label="Country" value={`${countryFlag(c.country)} ${countryName(c.country)}`} />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {c.phone && <CallButton phone={c.phone} clientId={c.id} className="btn-primary justify-center" testid="call-btn" />}
              <button className="btn-secondary justify-center" onClick={schedule} data-testid="schedule-btn"><Calendar className="w-4 h-4" /> Schedule</button>
            </div>
            {c.callback_at && <div className="mt-3 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Appointment: {format(new Date(c.callback_at), "MMM d, yyyy h:mm a")}</div>}
          </div>

          <div className="card p-5">
            <h3 className="font-semibold font-display text-slate-900 dark:text-white mb-4">Settings</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-400">Name</label>
                <input className="input mt-1" value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="edit-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wide text-slate-400">Type</label>
                  <Select value={form.client_type} onChange={(v) => upd("client_type", v)} options={[{ value: "lead", label: "Lead" }, { value: "client", label: "Client" }]} testid="edit-type" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-slate-400">Status</label>
                  <Select
                    value={isLead ? form.lead_status : form.client_status}
                    onChange={(v) => upd(isLead ? "lead_status" : "client_status", v)}
                    options={statuses.map((s) => ({ value: s.value, label: s.label }))} testid="edit-status" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wide text-slate-400">Email</label>
                  <input className="input mt-1" value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="edit-email" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-slate-400">Phone</label>
                  <input className="input mt-1" value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="edit-phone" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wide text-slate-400">Country</label>
                  <input className="input mt-1" value={form.country} onChange={(e) => upd("country", e.target.value.toUpperCase())} maxLength={2} data-testid="edit-country" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-slate-400">Source</label>
                  <Select value={form.source} onChange={(v) => upd("source", v)} placeholder="Default" options={sources.map((s) => ({ value: s.name, label: s.name }))} testid="edit-source" />
                </div>
                {isAdmin && (
                  <div>
                    <label className="text-xs uppercase tracking-wide text-slate-400">Assigned Agent</label>
                    <Select value={form.assigned_agent_id} onChange={(v) => upd("assigned_agent_id", v)} placeholder="Unassigned" options={agents.map((a) => ({ value: a.id, label: a.full_name }))} testid="edit-agent" />
                  </div>
                )}
              </div>
              <button className="btn-primary w-full" onClick={save} disabled={saving} data-testid="save-btn">{saving ? <Spinner className="w-4 h-4" /> : <><Save className="w-4 h-4" /> Save Changes</>}</button>
            </div>
          </div>
        </div>

        {/* Middle: comments */}
        <div className="lg:col-span-4">
          <div className="card p-5 flex flex-col h-full min-h-[400px]">
            <h3 className="font-semibold font-display text-slate-900 dark:text-white mb-4 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-indigo-500" /> Comments <span className="text-slate-400 font-normal">({d.comments.length})</span></h3>
            <div className="flex gap-2 mb-4">
              <input className="input" placeholder="Add a comment..." value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} data-testid="comment-input" />
              <button className="btn-primary px-3" onClick={addComment} data-testid="comment-submit"><Send className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-3 pr-1">
              {d.comments.length === 0 && <div className="text-center text-sm text-slate-400 py-8">No comments yet</div>}
              {d.comments.map((cm) => (
                <div key={cm.id} className="rounded-lg border border-slate-100 dark:border-slate-800 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{cm.agent_name || "Agent"}</span>
                    <span className="text-[11px] text-slate-400">{cm.created_at ? format(new Date(cm.created_at), "MMM d, h:mm a") : ""}</span>
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">{cm.content}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: deposits */}
        <div className="lg:col-span-4">
          <div className="card p-5">
            <h3 className="font-semibold font-display text-slate-900 dark:text-white mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-500" /> Deposit History <span className="text-slate-400 font-normal">({d.deposits.length})</span></h3>
            <div className="rounded-lg border border-slate-100 dark:border-slate-800 p-4 mb-4 space-y-3">
              <div className="text-sm font-medium text-slate-700 dark:text-slate-200">Add New Deposit</div>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="number" className="input pl-9" placeholder="Amount" value={dep.amount} onChange={(e) => setDep((s) => ({ ...s, amount: e.target.value }))} data-testid="deposit-amount" />
              </div>
              <input className="input" placeholder="Notes (optional)" value={dep.notes} onChange={(e) => setDep((s) => ({ ...s, notes: e.target.value }))} data-testid="deposit-notes" />
              <button className="btn-primary w-full" onClick={addDeposit} data-testid="deposit-submit"><DollarSign className="w-4 h-4" /> Add Deposit</button>
            </div>
            <div className="space-y-2">
              {d.deposits.length === 0 && <div className="text-center text-sm text-slate-400 py-6">No deposits yet</div>}
              {d.deposits.map((dp) => (
                <div key={dp.id} className="flex items-center justify-between rounded-lg border border-slate-100 dark:border-slate-800 px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="badge bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800">+{money(dp.amount)}</span>
                    <span className="text-xs text-slate-500">{dp.agent_name}</span>
                  </div>
                  <span className="text-[11px] text-slate-400">{dp.created_at ? format(new Date(dp.created_at), "MMM d, h:mm a") : ""}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
